import React, { Component } from "react";

import { logger } from "@formant/core/common/logger";
import { duration } from "@formant/core/model/duration";
import { ITeleopJoystickConfiguration } from "@formant/core/model/ITeleopJoystickConfiguration";

import { operateStore } from "../../stores";
import { Timer } from "../common/Timer";
import { WindowEvent } from "../common/WindowEvent";
import { deadzone } from "./TeleopModule/TeleopJoysticks/TeleopJoystick/deadzone";

export interface ITeleopActionsProps {
    configuration?: ITeleopJoystickConfiguration;

    onCancelGoal?: () => void;
    onStop?: () => void;

    onLeftJoystickPressed?: () => void;
    onLeftJoystickReleased?: () => void;
    onLeftJoystickUp?: (value: number) => void;
    onLeftJoystickDown?: (value: number) => void;
    onLeftJoystickLeft?: (value: number) => void;
    onLeftJoystickRight?: (value: number) => void;
    onLeftJoystickXAxis?: (value: number) => void;
    onLeftJoystickYAxis?: (value: number) => void;

    onRightJoystickPressed?: () => void;
    onRightJoystickReleased?: () => void;
    onRightJoystickUp?: (value: number) => void;
    onRightJoystickDown?: (value: number) => void;
    onRightJoystickLeft?: (value: number) => void;
    onRightJoystickRight?: (value: number) => void;
    onRightJoystickXAxis?: (value: number) => void;
    onRightJoystickYAxis?: (value: number) => void;

    onDownKeysChanged?: (downKeys: string[]) => void;
}

export interface ITeleopActionMappings {
    [key: string]: ITeleopActionMapping;
}

export interface ITeleopActionMapping {
    key?: string;
    gamepadButtons?: number[];
    gamepadAxis?: number;
    onPressed?: () => void;
    onReleased?: () => void;
    onValue?: (value: number) => void;
}

const buttonOnOffThreshold = 0.5;
const gamepadPollingLoopInterval = duration.second / 60;

export class TeleopActions extends Component<ITeleopActionsProps> {
    private mappings: ITeleopActionMappings = {};

    private downKeys: string[] = [];
    private downGamepadButtons: number[] = [];
    private lastGamepadButtons: GamepadButton[] = [];
    private hasInteractedWithGamepad: boolean = false;
    private gamepadPreviouslyDetected = false;
    private windowFocused = true;

    public componentDidMount() {
        this.setMappings();
    }

    public componentDidUpdate() {
        this.setMappings();
    }

    public render() {
        const { configuration } = this.props;
        if (configuration?.disableGamepadAndKeyboard) {
            return false;
        }
        return (
            <>
                <WindowEvent type="keydown" onEvent={this.onKeyDown} />
                <WindowEvent type="blur" onEvent={this.onBlur} />
                <WindowEvent type="focus" onEvent={this.onFocus} />
                <WindowEvent type="keyup" onEvent={this.onKeyUp} />
                <Timer
                    interval={gamepadPollingLoopInterval}
                    onTick={this.onPollGamepad}
                />
            </>
        );
    }

    private onPollGamepad = () => {
        const { downGamepadButtons } = this;
        const { gamepad } = operateStore;

        if (!gamepad) {
            if (this.gamepadPreviouslyDetected) {
                this.gamepadPreviouslyDetected = false;
                this.props.onLeftJoystickXAxis?.(0);
                this.props.onLeftJoystickYAxis?.(0);
                this.props.onRightJoystickXAxis?.(0);
                this.props.onRightJoystickYAxis?.(0);
                this.props.onLeftJoystickDown?.(0);
                this.props.onLeftJoystickUp?.(0);
                this.props.onLeftJoystickReleased?.();
            }
            return;
        }
        this.gamepadPreviouslyDetected = true;

        // prevent joystick events if the window is not focused.
        // this is especially important if a user has multiple teleop sessions open
        if (!this.windowFocused) {
            return;
        }

        const { buttons, axes } = gamepad;
        const gamepadButtonActions = this.getGamepadButtonMappings(buttons);
        gamepadButtonActions.forEach(
            ({ gamepadButtons, onPressed, onReleased, onValue }) => {
                if (gamepadButtons === undefined) {
                    return;
                }
                gamepadButtons.forEach(gamepadButton => {
                    const value = deadzone(buttons[gamepadButton].value);

                    if (
                        value !== 0 &&
                        this.hasInteractedWithGamepad === false
                    ) {
                        this.hasInteractedWithGamepad = true;
                    }

                    const isAlreadyDown =
                        downGamepadButtons.includes(gamepadButton);

                    const lastValue = deadzone(
                        this.lastGamepadButtons[gamepadButton]?.value || 0,
                    );

                    if (!this.hasInteractedWithGamepad) {
                        return;
                    }

                    if (value > buttonOnOffThreshold && !isAlreadyDown) {
                        this.downGamepadButtons.push(gamepadButton);
                        onPressed?.();
                    }

                    if (value <= buttonOnOffThreshold) {
                        this.downGamepadButtons =
                            this.downGamepadButtons.filter(
                                _ => _ !== gamepadButton,
                            );
                        if (isAlreadyDown) {
                            onReleased?.();
                        }
                    }

                    if (lastValue !== value) {
                        onValue?.(value);
                    }
                });
            },
        );
        this.lastGamepadButtons = [...buttons];

        const gamepadAxisActions = this.getGamepadAxisMappings(axes);
        gamepadAxisActions.forEach(({ gamepadAxis, onValue }) => {
            if (gamepadAxis === undefined) {
                return;
            }

            const value = deadzone(axes[gamepadAxis]);

            if (value !== 0 && this.hasInteractedWithGamepad === false) {
                this.hasInteractedWithGamepad = true;
            }

            if (this.hasInteractedWithGamepad) {
                onValue?.(value);
            }
        });
    };

    private onKeyDown = (event: KeyboardEvent) => {
        if (event.key === undefined) {
            logger.error(
                "onKeyDown KeyboardEvent had undefined 'key' property in TeleopActions",
            );
            return;
        }

        const key = event.key.toLowerCase();

        if (this.downKeys.indexOf(key) !== -1) {
            return;
        }

        this.downKeys.push(key);
        this.getKeyMappings(key).forEach(_ => {
            _.onValue?.(1);
            _.onPressed?.();
        });

        this.props.onDownKeysChanged?.(this.downKeys);
    };

    private onBlur = (_: FocusEvent) => {
        this.windowFocused = false;
        this.downKeys.forEach(key => {
            this.getKeyMappings(key).forEach(keyMapping => {
                keyMapping.onValue?.(0);
                keyMapping.onReleased?.();
            });
        });
        this.props.onStop?.();
    };

    private onFocus = (_: FocusEvent) => {
        this.windowFocused = true;
    };

    private onKeyUp = (event: KeyboardEvent) => {
        if (event.key === undefined) {
            logger.error(
                "onKeyUp KeyboardEvent had undefined 'key' property in TeleopActions",
            );
            return;
        }

        const key = event.key.toLowerCase();

        this.getKeyMappings(key).forEach(_ => {
            _.onValue?.(0);
            _.onReleased?.();
        });

        this.downKeys = this.downKeys.filter(_ => _ !== key);
        this.props.onDownKeysChanged?.(this.downKeys);
    };

    private getKeyMappings(key: string): ITeleopActionMapping[] {
        const { mappings } = this;

        return (
            Object.keys(mappings).filter(_ => mappings[_]?.key === key) || []
        ).map(_ => mappings[_]);
    }

    private getGamepadButtonMappings(
        buttons: readonly GamepadButton[],
    ): ITeleopActionMapping[] {
        const { mappings } = this;

        return Object.keys(mappings)
            .filter(mapping =>
                buttons.some((_, index) =>
                    mappings[mapping]?.gamepadButtons?.includes(index),
                ),
            )
            .map(_ => mappings[_]);
    }

    private getGamepadAxisMappings(
        axes: readonly number[],
    ): ITeleopActionMapping[] {
        const { mappings } = this;

        return Object.keys(mappings)
            .filter(mapping =>
                axes.some(
                    (_, index) => mappings[mapping]?.gamepadAxis === index,
                ),
            )
            .map(_ => mappings[_]);
    }

    private setMappings() {
        const {
            configuration,
            onCancelGoal,
            onLeftJoystickPressed,
            onLeftJoystickReleased,
            onLeftJoystickUp,
            onLeftJoystickDown,
            onLeftJoystickLeft,
            onLeftJoystickRight,
            onLeftJoystickXAxis,
            onLeftJoystickYAxis,
            onRightJoystickPressed,
            onRightJoystickReleased,
            onRightJoystickUp,
            onRightJoystickDown,
            onRightJoystickLeft,
            onRightJoystickRight,
            onRightJoystickXAxis,
            onRightJoystickYAxis,
        } = this.props;
        this.mappings = {
            cancelGoal: {
                key: "Escape",
                onPressed: () => onCancelGoal?.(),
            },
            leftJoystickUp: {
                key: "w",
                gamepadButtons: configuration?.disableTriggerJoystickMapping
                    ? undefined
                    : [7],
                onPressed: onLeftJoystickPressed,
                onReleased: onLeftJoystickReleased,
                onValue: onLeftJoystickUp,
            },
            leftJoystickDown: {
                key: "s",
                gamepadButtons: configuration?.disableTriggerJoystickMapping
                    ? undefined
                    : [6],
                onPressed: onLeftJoystickPressed,
                onReleased: onLeftJoystickReleased,
                onValue: onLeftJoystickDown,
            },
            leftJoystickLeft: {
                key: "a",
                onPressed: onLeftJoystickPressed,
                onReleased: onLeftJoystickReleased,
                onValue: onLeftJoystickLeft,
            },
            leftJoystickRight: {
                key: "d",
                onPressed: onLeftJoystickPressed,
                onReleased: onLeftJoystickReleased,
                onValue: onLeftJoystickRight,
            },
            leftJoystickXAxis: {
                gamepadAxis: configuration?.x?.gamepadAxis,
                onValue: onLeftJoystickXAxis,
            },
            leftJoystickYAxis: {
                gamepadAxis: configuration?.y?.gamepadAxis,
                onValue: onLeftJoystickYAxis,
            },
            rightJoystickUp: {
                key: "arrowup",
                onPressed: onRightJoystickPressed,
                onReleased: onRightJoystickReleased,
                onValue: onRightJoystickUp,
            },
            rightJoystickDown: {
                key: "arrowdown",
                onPressed: onRightJoystickPressed,
                onReleased: onRightJoystickReleased,
                onValue: onRightJoystickDown,
            },
            rightJoystickLeft: {
                key: "arrowleft",
                onPressed: onRightJoystickPressed,
                onReleased: onRightJoystickReleased,
                onValue: onRightJoystickLeft,
            },
            rightJoystickRight: {
                key: "arrowright",
                onPressed: onRightJoystickPressed,
                onReleased: onRightJoystickReleased,
                onValue: onRightJoystickRight,
            },
            rightJoystickXAxis: {
                gamepadAxis: configuration?.x?.gamepadAxis,
                onValue: onRightJoystickXAxis,
            },
            rightJoystickYAxis: {
                gamepadAxis: configuration?.y?.gamepadAxis,
                onValue: onRightJoystickYAxis,
            },
        };
    }
}
